/**
 * Crescent — Industries listing (card grid).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CrescentIndustriesIndex() {
  const [items, setItems] = useState(null);

  usePageSeo({
    title: "Industries We Serve — Crescent Flexi Pack",
    description: "Flexible packaging for seafood, meat, tea, coffee, spices, snacks, rice, ice cream, pet food, agriculture and private-label buyers worldwide.",
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/crescent" },
    { name: "Industries", url: "/crescent/industries" },
  ]);

  useEffect(() => {
    axios.get(`${API}/crescent/industries`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  return (
    <CrescentLayout>
      <div data-testid="crescent-industries-index">
        <section className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-xs text-amber-100 mb-3">
              <Link to="/crescent" className="hover:underline">Home</Link>
              <span className="mx-2">›</span>
              <span className="text-white">Industries</span>
            </nav>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight">Industries We Serve</h1>
            <p className="mt-3 text-white/90 max-w-2xl text-lg">
              Reliable packaging solutions across ten industry verticals — from seafood exporters to supermarket private-label.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {items === null && <div className="text-center py-10 text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="industries-list">
              {(items || []).map((i) => (
                <Link
                  key={i.slug}
                  to={`/crescent/industries/${i.slug}`}
                  data-testid={`industry-card-${i.slug}`}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-emerald-500 hover:shadow-xl transition-all flex flex-col"
                >
                  <div className={`h-44 bg-slate-100 bg-cover bg-center bg-gradient-to-br ${i.fallback_gradient || 'from-blue-700 to-slate-900'}`}
                    style={{ backgroundImage: `url(${i.image})` }} />
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-heading font-bold text-slate-900 text-lg group-hover:text-emerald-700">{i.title.replace(/ Packaging$/, "")}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed line-clamp-3">{i.tagline}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 pt-4">
                      Explore sector <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}
